/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import ReactCountryFlag from 'react-country-flag';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { intl } from 'i18n';

import { CurriculumsApi, ItemsApi } from 'global/api';
import {
  CurriculumDetail,
  CurriculumModules,
  ModuleHierarchy,
} from 'labxchange-client';
import { Button, PillButton, showErrorMessage } from 'ui/components';
import {
  WrappedMessage,
  getHomeVariationExperimentValue,
  useLayoutSize,
  renderSubModulesList,
  isLocalOrReviewApp,
} from 'utils';
import { ROUTES } from 'global/constants';
import { analyticsInstance, EVENT_NAMES } from 'tracking';
import { ModuleSearchBar } from 'curriculum/components/ModuleSearchBar';
import uiMessages from 'ui/components/displayMessages';

import messages from '../../displayMessages';
import CommonContent from './CommonContent';
import { PlainAssetCard, PlainAssetCardSkeleton } from './PlainAssetCard';

export const CURRICULUM_ASSET_MAPPINGS: any = {
  ncert: {
    biology: {
      'class-xi': [
        'lb:LabXchange:21c1cb80-e0c2-3c8b-ae10-f6775e43b81f:html:1',
        'lb:LabXchange:8e3b1a74:video:1',
        'lb:LabXchange:7e57d034:lx_simulation:1',
        'lb:LabXchange:fbdb87f4:lx_simulation:1',
      ],
      'class-xii': [
        'lb:LabXchange:5ac78a28:html:1',
        'lb:LabXchange:b616dadc:video:1',
        'lb:LabXchange:1fb31712:lx_simulation:1',
        'lb:LabXchange:7c77110e:lx_simulation:1',
      ],
    },
    chemistry: {
      'class-xi': [
        'lb:LabXchange:46aeb0dc:html:1',
        'lb:LabXchange:d4da90e1:video:1',
        'lb:LabXchange:0ee38d28:lx_simulation:1',
        'lb:LabXchange:a6782bb7:lx_simulation:1',
      ],
      'class-xii': [
        'lb:LabXchange:95f38a53:video:1',
        'lb:LabXchange:3fd6fcc2:video:1',
        'lb:LabXchange:807e0eae:lx_simulation:1',
        'lb:LabXchange:626b1dac:lx_simulation:1',
      ],
    },
  },
};

interface HomeVariationFourProps {
  curriculumSlug: string;
  countryCode: string;
  isUserLoggedIn: boolean;
}

export const HomeVariationFour = ({
  curriculumSlug,
  isUserLoggedIn,
  countryCode,
}: HomeVariationFourProps) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const experimentVariation = getHomeVariationExperimentValue(curriculumSlug);
  const layoutSize = useLayoutSize();
  const isMobile = ['small'].includes(layoutSize);

  const [loading, setLoading] = useState<boolean>(true);
  const [curriculum, setCurriculum] = useState<CurriculumDetail | undefined>(
    undefined
  );

  const [modulesLoading, setModulesLoading] = useState<boolean>(true);
  const [curriculumModules, setCurriculumModules] = useState<
    Record<string, ModuleHierarchy[]>
  >({});
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [assetsByGrade, setAssetsByGrade] = useState<Record<string, any[]>>({});
  const [selectedSubject, setSelectedSubject] = useState<string>(
    queryParams.get('subject') || ''
  );
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  useEffect(() => {
    const fetchCurriculumData = async () => {
      setLoading(true);
      try {
        const response: CurriculumDetail = await CurriculumsApi.getDetail({
          id: curriculumSlug,
        });
        setCurriculum(response);
        const firstSubject = response.subjects?.[0];
        if (!selectedSubject) {
          setSelectedSubject(firstSubject?.slug || '');
        }
      } catch (error) {
        setLoading(false);
        setCurriculum(undefined);
        showErrorMessage(
          <WrappedMessage message={messages.homeVariationPageCurriculumError} />
        );
      }
      setLoading(false);
    };

    fetchCurriculumData();
  }, [curriculumSlug]);

  useEffect(() => {
    const fetchDataWithModules = async () => {
      if (!curriculum || !selectedSubject) return;

      setModulesLoading(true);

      const subjectDetail = curriculum.subjects?.find(
        (subject) => subject.slug === selectedSubject
      );

      if (!subjectDetail?.grades?.length) {
        setCurriculumModules({});
        setModulesLoading(false);
        return;
      }

      try {
        const gradeModuleResponses = await Promise.all(
          subjectDetail.grades.map(async (grade) => {
            const response: CurriculumModules = await CurriculumsApi.read({
              id: curriculum.slug,
              subject: selectedSubject,
              grade: grade.slug,
            });
            return { gradeSlug: grade.slug, modules: response.modules || [] };
          })
        );

        const modulesByGrade = gradeModuleResponses.reduce(
          (acc, { gradeSlug, modules }) => {
            acc[gradeSlug] = modules;
            return acc;
          },
          {} as Record<string, ModuleHierarchy[]>
        );

        setCurriculumModules(modulesByGrade);
      } catch (error) {
        showErrorMessage(
          intl.formatMessage(messages.homeVariationPageModulesError)
        );
        setCurriculumModules({});
      }

      setModulesLoading(false);
    };

    fetchDataWithModules();
  }, [curriculum, selectedSubject]);

  useEffect(() => {
    const fetchAssetsData = async () => {
      if (!curriculum || !selectedSubject) return;

      setAssetsLoading(true);
      setAssetsByGrade({});

      try {
        const subjectDetail = curriculum.subjects?.find(
          (subject) => subject.slug === selectedSubject
        );

        if (!subjectDetail?.grades?.length) {
          setAssetsByGrade({});
          setAssetsLoading(false);
          return;
        }

        const gradeAssetsResponses = await Promise.all(
          subjectDetail.grades.map(async (grade) => {
            const gradeSlug = grade.slug;
            const assetIds = isLocalOrReviewApp()
              ? Array(4).fill('lb:LabXchange:1fb8b9d6:lx_simulation:2')
              : CURRICULUM_ASSET_MAPPINGS?.[curriculumSlug]?.[
                  selectedSubject
                ]?.[gradeSlug] || [];

            const assetDetails = await Promise.all(
              assetIds.map((assetId: string) => ItemsApi.read({ id: assetId }))
            );

            return { gradeSlug, assets: assetDetails };
          })
        );

        const assetsGrouped: Record<string, any[]> =
          gradeAssetsResponses.reduce((acc: any, { gradeSlug, assets }) => {
            acc[gradeSlug] = assets;
            return acc;
          }, {});

        setAssetsByGrade(assetsGrouped);
      } catch (error) {
        setAssetsByGrade({});
        showErrorMessage(
          <WrappedMessage message={messages.homeVariationPageAssetsError} />
        );
      }

      setAssetsLoading(false);
    };

    fetchAssetsData();
  }, [selectedSubject, curriculum]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedSubject) params.set('subject', selectedSubject);
    window.history.replaceState(null, '', `?${params.toString()}`);
  }, [selectedSubject]);

  const handleFindResourcesClick = () => {
    analyticsInstance.track(
      EVENT_NAMES.NewHomeSearchBarButtonClicked(experimentVariation),
      {
        curriculum_slug: curriculumSlug,
        country: countryCode,
        experiment_variation: experimentVariation,
        search_keyword: searchKeyword,
        button_text: intl.formatMessage(
          messages.homeVariationFindResourcesBtnLabel
        ),
        url: window.location.toString(),
      }
    );
    history.push(`${ROUTES.Library.HOME}?q=${searchKeyword}`);
  };

  const onPillClick = (subjectSlug: string) => {
    analyticsInstance.track(
      EVENT_NAMES.NewHomeSubjectPillClicked(experimentVariation),
      {
        curriculum_slug: curriculumSlug,
        country: countryCode,
        experiment_variation: experimentVariation,
        search_keyword: searchKeyword,
        subject_slug: subjectSlug,
        url: window.location.toString(),
      }
    );
    setSelectedSubject(subjectSlug);
  };

  const onModuleClick = (module: ModuleHierarchy, grade: any) =>
    analyticsInstance.track(
      EVENT_NAMES.NewHomePageSubModuleClicked(experimentVariation),
      {
        module_id: module.id,
        module_slug: module.slug,
        module_name: module.name,
        module_url: ROUTES.Curriculum.MODULE_SLUG(
          curriculumSlug,
          selectedSubject,
          grade.slug,
          module.slug
        ),
        search_keyword: searchKeyword,
        subject_slug: selectedSubject,
        grade_slug: grade.slug,
        curriculum_slug: curriculumSlug,
        country: countryCode,
        experiment_variation: experimentVariation,
        url: window.location.toString(),
      }
    );

  const renderHeroSection = () => (
    <div className='hc-var-four-hero-section'>
      <div className='hero-hexagon-left' tabIndex={-1}>
        <img
          src='/assets/images/hexagon-hero-left.svg'
          alt={intl.formatMessage(messages.homeVariationPageBgMapAlt, {
            curriculumSlug,
          })}
        />
      </div>
      <div className='hero-hexagon-right' tabIndex={-1}>
        <img
          src='/assets/images/hexagon-hero-right.svg'
          alt={intl.formatMessage(messages.homeVariationPageBgFlagAlt, {
            curriculumSlug,
          })}
        />
      </div>
      <div className='harvard-logo'>
        <img
          src='/assets/images/harvard-logo-white.svg'
          alt={intl.formatMessage(uiMessages.uiHarvardOnlineLogo)}
        />
      </div>
      <div className='hero-content-wrapper'>
        <div className='hero-content'>
          <div className='hero-content-image'>
            <img
              src={`/assets/images/${curriculumSlug}-hero-illustration-${isMobile ? 'mobile' : 'desktop'}.png`}
              alt={intl.formatMessage(
                messages.homeVariationPageHeroIllustrationAlt,
                { curriculumSlug }
              )}
            />
          </div>
          <h1 className='hero-content-heading'>
            Empower Your Science Lessons with{' '}
            <span className='highlight-word'>Free, Interactive</span> Learning
            Resources
          </h1>
          <div className='hero-content-search-bar-wrapper'>
            <ModuleSearchBar
              value={searchKeyword}
              onChange={(value) => setSearchKeyword(value)}
              placeholder={uiMessages.uiNavSearch}
              placeHolderValues={{ count: 27000 }}
              onPressEnter={handleFindResourcesClick}
            />
            <Button
              className='hero-content-search-bar-btn'
              label={messages.homeVariationFindResourcesBtnLabel}
              onClick={handleFindResourcesClick}
              icon='arrow-right-thin'
              iconPosition='right'
              btnStyle='unstyled'
              data-testid='find-resources-btn'
            />
          </div>
          <p className='hero-content-subtitle'>
            {intl.formatMessage(messages.homeVariationTrustedByText)}
          </p>
        </div>
      </div>
    </div>
  );

  const renderContentSection = () => {
    return (
      <div className='hc-var-four-content-section'>
        <div className='content-hexagon-left' tabIndex={-1}>
          <img
            src='/assets/images/navy-hexagon-group.svg'
            alt={intl.formatMessage(messages.homeVariationPageBgMapAlt, {
              curriculumSlug,
            })}
          />
        </div>
        <div className='hc-var-four-content-section-header'>
          <span className='content-section-header-flag'>
            {loading ? (
              <Skeleton width={70} height={48} />
            ) : (
              <ReactCountryFlag
                countryCode={countryCode}
                svg
                style={{
                  width: '70px',
                  height: '48px',
                }}
              />
            )}
          </span>
          <h2 className='content-section-header-title'>
            {loading ? (
              <div className='content-section-header-title-skeleton'>
                <Skeleton width='100%' height={50} />
                <Skeleton width='100%' height={50} />
              </div>
            ) : (
              intl.formatMessage(messages.homeVariationContentSectionHeading, {
                curriculumSlug: curriculum?.slug.toUpperCase(),
                country: (curriculum?.country as any).name,
              })
            )}
          </h2>
          <div className='content-section-header-pill-btns'>
            {loading
              ? Array.from({ length: isMobile ? 2 : 3 }, (_, index) => (
                  <Skeleton
                    key={index}
                    className='pill-button'
                    width={125}
                    height={55}
                  />
                ))
              : curriculum?.subjects?.map((subject) => {
                  if (subject.isPublic) {
                    const isSelected = selectedSubject === subject.slug;
                    return (
                      <PillButton
                        key={subject.slug}
                        label={subject.name}
                        onClick={() => onPillClick(subject.slug)}
                        ariaLabel={subject.name}
                        isSelected={isSelected}
                      />
                    );
                  }
                  return null;
                })}
          </div>
        </div>
        <div className='hc-var-four-content-section-modules'>
          {loading || modulesLoading ? (
            <div className='grade-section'>
              <h1 className='grade-section-heading'>
                <Skeleton width={100} height={40} />
              </h1>
              <div className='sub-modules-list'>
                {Array.from({ length: 3 }, (_, index) => (
                  <div className='modules-group' key={index}>
                    <div className='font-xs-lt sub-module-name'>
                      <Skeleton width={200} height={28} />
                    </div>
                    <div className='sub-sub-modules-list'>
                      {/* tslint:disable-next-line:no-shadowed-variable */}
                      {Array.from({ length: 4 }, (_, index) => (
                        <div className='sub-sub-module-name' key={index}>
                          <Skeleton width={250} height={23} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className='grade-section-assets'>
                {Array.from({ length: 4 }, (_, index) => (
                  <PlainAssetCardSkeleton key={index} />
                ))}
              </div>
              <div className='grade-section-explore-all-btn-wrapper'>
                <Skeleton
                  className='grade-section-explore-all-btn'
                  width={250}
                  height={50}
                />
              </div>
            </div>
          ) : (
            curriculum?.subjects?.map((subject) => {
              if (subject.slug === selectedSubject) {
                return subject.grades?.map((grade) => {
                  return (
                    <div className='grade-section' key={grade.slug}>
                      <h1 className='grade-section-heading'>{grade.name}</h1>
                      {renderSubModulesList(
                        (curriculumModules[grade.slug] || []).slice(0, 3),
                        curriculumSlug,
                        subject.slug,
                        grade.slug,
                        1,
                        (module) => onModuleClick(module, grade),
                        true
                      )}
                      <div className='grade-section-assets'>
                        {assetsLoading
                          ? Array.from({ length: 4 }, (_, index) => (
                              <PlainAssetCardSkeleton key={index} />
                            ))
                          : (assetsByGrade[grade.slug] || [])
                              .slice(0, 4)
                              .map((asset) => (
                                <PlainAssetCard
                                  key={asset.id}
                                  asset={asset}
                                  curriculumSlug={curriculumSlug}
                                  trackingData={{
                                    curriculum_slug: curriculumSlug,
                                    country: countryCode,
                                    subject_sllug: subject.slug,
                                    grade_slug: grade.slug,
                                    search_keyword: searchKeyword,
                                    experiment_variation: experimentVariation,
                                  }}
                                />
                              ))}
                      </div>
                      <div className='grade-section-explore-all-btn-wrapper'>
                        <Button
                          className='grade-section-explore-all-btn'
                          label={messages.homeVariationExploreAllTopicsBtnLabel}
                          href={`/c/${curriculumSlug}?subject=${subject.slug}&grade=${grade.slug}`}
                          onClick={() => {
                            analyticsInstance.track(
                              EVENT_NAMES.NewHomeExploreAllTopicsButtonClicked(
                                experimentVariation
                              ),
                              {
                                curriculum_slug: curriculumSlug,
                                country: countryCode,
                                experiment_variation: experimentVariation,
                                subject_slug: subject.slug,
                                grade_slug: grade.slug,
                                button_text: intl.formatMessage(
                                  messages.homeVariationExploreAllTopicsBtnLabel
                                ),
                                search_keyword: searchKeyword,
                                url: window.location.toString(),
                              }
                            );
                          }}
                          icon='arrow-right-thin'
                          iconPosition='right'
                          btnStyle='unstyled'
                          data-testid='explore-all-topics-btn'
                        />
                      </div>
                    </div>
                  );
                });
              }
              return null;
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderHeroSection()}
      {renderContentSection()}
      <CommonContent
        isUserLoggedIn={isUserLoggedIn}
        curriculumSlug={curriculumSlug}
        countryCode={countryCode}
      />
    </>
  );
};

export default HomeVariationFour;
